<script setup>
import { computed, onBeforeUnmount, onMounted, ref, shallowRef } from 'vue'
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  Bars3Icon,
  CheckCircleIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline'
import { useI18n } from '../i18n'
import {
  detectPwaPlatform,
  isStandaloneDisplay,
  readInstallDismissal,
  shouldShowInstallHelper,
  writeInstallDismissal,
} from '../pwaInstall'

const { t } = useI18n()
const previewPlatform = import.meta.env.DEV
  ? new URLSearchParams(window.location.search).get('pwa-install-preview')
  : ''
const platform = ref(['android', 'ios'].includes(previewPlatform)
  ? previewPlatform
  : detectPwaPlatform(navigator))
const deferredPrompt = shallowRef(null)
const standalone = ref(isStandaloneDisplay(window, navigator))
const dismissed = ref(previewPlatform ? false : readInstallDismissal(window.localStorage))
const installing = ref(false)
const displayMode = window.matchMedia('(display-mode: standalone)')

const isIos = computed(() => platform.value === 'ios')
const androidInstallAvailable = computed(() => Boolean(deferredPrompt.value) || previewPlatform === 'android')
const visible = computed(() => shouldShowInstallHelper({
  platform: platform.value,
  dismissed: dismissed.value,
  standalone: standalone.value,
}))
const title = computed(() => t(isIos.value ? 'pwaInstallIosTitle' : 'pwaInstallAndroidTitle'))
const subtitle = computed(() => {
  if (isIos.value) return t('pwaInstallIosSubtitle')
  return t(androidInstallAvailable.value ? 'pwaInstallAndroidSubtitle' : 'pwaInstallAndroidManualSubtitle')
})
const steps = computed(() => {
  if (isIos.value) {
    return [
      { key: 'share', label: t('pwaInstallIosShare'), icon: ArrowUpTrayIcon },
      { key: 'home', label: t('pwaInstallIosHome'), icon: PlusIcon },
      { key: 'open', label: t('pwaInstallIosAdd'), appIcon: true },
    ]
  }
  if (androidInstallAvailable.value) {
    return [
      { key: 'install', label: t('pwaInstallAndroidInstall'), icon: ArrowDownTrayIcon },
      { key: 'confirm', label: t('pwaInstallAndroidConfirm'), icon: CheckCircleIcon },
      { key: 'open', label: t('pwaInstallAndroidOpen'), appIcon: true },
    ]
  }
  return [
    { key: 'menu', label: t('pwaInstallAndroidMenu'), icon: Bars3Icon },
    { key: 'menu-install', label: t('pwaInstallAndroidMenuInstall'), icon: PlusIcon },
    { key: 'menu-confirm', label: t('pwaInstallAndroidMenuConfirm'), icon: CheckCircleIcon },
  ]
})

function dismiss() {
  dismissed.value = true
  writeInstallDismissal(window.localStorage)
}

function handleBeforeInstallPrompt(event) {
  event.preventDefault()
  deferredPrompt.value = event
}

function handleInstalled() {
  standalone.value = true
  deferredPrompt.value = null
}

function handleDisplayModeChange() {
  standalone.value = isStandaloneDisplay(window, navigator)
}

async function installAndroid() {
  if (installing.value) return
  if (previewPlatform === 'android' && !deferredPrompt.value) {
    standalone.value = true
    return
  }

  const promptEvent = deferredPrompt.value
  if (!promptEvent) return
  installing.value = true
  try {
    await promptEvent.prompt()
    const choice = await promptEvent.userChoice
    if (choice?.outcome === 'accepted') {
      standalone.value = true
    } else {
      dismiss()
    }
  } finally {
    deferredPrompt.value = null
    installing.value = false
  }
}

onMounted(() => {
  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  window.addEventListener('appinstalled', handleInstalled)
  displayMode.addEventListener?.('change', handleDisplayModeChange)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  window.removeEventListener('appinstalled', handleInstalled)
  displayMode.removeEventListener?.('change', handleDisplayModeChange)
})
</script>

<template>
  <Transition name="pwa-install">
    <aside v-if="visible" class="pwa-install-card" :class="`platform-${platform}`"
      :aria-labelledby="`pwa-install-title-${platform}`">
      <header class="pwa-install-header">
        <img class="pwa-install-app-icon" src="/pwa-192x192.png" :alt="t('pwaInstallIconAlt')" />
        <div class="pwa-install-heading">
          <strong :id="`pwa-install-title-${platform}`">{{ title }}</strong>
          <span>{{ subtitle }}</span>
        </div>
        <button class="pwa-install-close" type="button" :aria-label="t('pwaInstallDismiss')" @click="dismiss">
          <XMarkIcon aria-hidden="true" />
        </button>
      </header>

      <ol class="pwa-install-steps">
        <li v-for="step in steps" :key="step.key">
          <span class="pwa-install-step-icon" aria-hidden="true">
            <img v-if="step.appIcon" src="/pwa-192x192.png" alt="" />
            <component :is="step.icon" v-else />
          </span>
          <span>{{ step.label }}</span>
        </li>
      </ol>

      <button v-if="!isIos && androidInstallAvailable" class="button pwa-install-action" type="button"
        :disabled="installing" @click="installAndroid">
        {{ installing ? t('pwaInstallInstalling') : t('pwaInstallAction') }}
      </button>
    </aside>
  </Transition>
</template>
